import React, { useEffect, useCallback, useState, useRef } from 'react';
import { Box, Text, IconButton } from '@chakra-ui/react';
import { CloseIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import ThemePhoto from './ThemePhoto';
import { Gallery } from '../data/photos';
import './PortfolioLightbox.css';

interface PortfolioLightboxProps {
  category: Gallery;
  onClose: () => void;
}

const pad = (n: number) => String(n).padStart(2, '0');

const PortfolioLightbox: React.FC<PortfolioLightboxProps> = ({ category, onClose }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const thumbRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const count = category.photos.length;

  const go = useCallback(
    (index: number) => setActiveIndex((index + count) % count),
    [count]
  );
  const prev = useCallback(() => go(activeIndex - 1), [activeIndex, go]);
  const next = useCallback(() => go(activeIndex + 1), [activeIndex, go]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, prev, next]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Aktivní náhled drží v zorném poli a sousední fotky načte dopředu
  useEffect(() => {
    thumbRefs.current[activeIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
    if (count < 2) return;
    [(activeIndex + 1) % count, (activeIndex - 1 + count) % count].forEach(i => {
      ['', '-bw'].forEach(variant => {
        const img = new window.Image();
        img.src = `${category.photos[i]}${variant}.webp`;
      });
    });
  }, [activeIndex, category.photos, count]);

  // Touch / swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) diff > 0 ? next() : prev();
    touchStartX.current = null;
  };

  return (
    <Box className="lb-overlay" onClick={onClose}>
      {/* Horní lišta */}
      <Box className="lb-topbar" onClick={e => e.stopPropagation()}>
        <Text className="lb-title">{category.title}</Text>
        <Box className="lb-topbar-right">
          <Text className="lb-counter">
            <span className="lb-counter-now">{pad(activeIndex + 1)}</span>
            <span className="lb-counter-sep">/</span>
            <span className="lb-counter-total">{pad(count)}</span>
          </Text>
          <IconButton
            aria-label="Zavřít"
            icon={<CloseIcon boxSize="11px" />}
            onClick={onClose}
            className="lb-close"
            variant="unstyled"
          />
        </Box>
      </Box>

      {/* Hlavní fotka */}
      <Box
        className="lb-stage"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <ThemePhoto
          key={category.photos[activeIndex]}
          base={category.photos[activeIndex]}
          alt={`${category.title} ${activeIndex + 1}`}
          fit="contain"
          eager
          className="lb-image"
        />
      </Box>

      {count > 1 && (
        <>
          <IconButton
            aria-label="Předchozí"
            icon={<ChevronLeftIcon boxSize="26px" />}
            onClick={e => { e.stopPropagation(); prev(); }}
            className="lb-arrow lb-arrow--left"
            variant="unstyled"
          />
          <IconButton
            aria-label="Další"
            icon={<ChevronRightIcon boxSize="26px" />}
            onClick={e => { e.stopPropagation(); next(); }}
            className="lb-arrow lb-arrow--right"
            variant="unstyled"
          />
        </>
      )}

      {/* Pásek náhledů */}
      {count > 1 && (
        <Box className="lb-thumbs" onClick={e => e.stopPropagation()}>
          {category.photos.map((base, i) => (
            <Box
              as="button"
              key={base}
              ref={(el: HTMLButtonElement | null) => (thumbRefs.current[i] = el)}
              className={`lb-thumb ${i === activeIndex ? 'lb-thumb--active' : ''}`}
              onClick={() => go(i)}
              aria-label={`Fotografie ${i + 1}`}
            >
              <ThemePhoto base={base} alt="" thumb className="lb-thumb-img" />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PortfolioLightbox;
